import { EnvironmentInjector, inject, Injectable } from '@angular/core';
import { NavigateToEventActionDto } from '../model';
import { AgentPanelHostService } from './agent-panel-host.service';

/**
 * Handles the `NavigateToEvent` action: opens the calendar event preview in the right sidenav of the
 * current page, or of the events dashboard of the target space when the user is elsewhere.
 *
 * The preview lives in `feature-event`, which a feature library may not depend on statically; it is
 * loaded lazily like the asset panel.
 */
@Injectable({ providedIn: 'root' })
export class AgentEventNavigationService {
  private readonly _host = inject(AgentPanelHostService);
  // eslint-disable-next-line @nx/enforce-module-boundaries
  private _featureEventChunk?: Promise<typeof import('feature-event')>;
  private _panelInjector?: EnvironmentInjector;

  async navigate(action: NavigateToEventActionDto): Promise<void> {
    const portal = await this._host.portalFor(action.apartmentId, [
      '/space',
      action.apartmentId,
      'dashboard',
      'events'
    ]);
    if (!portal) throw new Error('[ai-agent] no panel host for the event');

    // eslint-disable-next-line @nx/enforce-module-boundaries
    const { EventPanelService, EventAccessService } = await (this._featureEventChunk ??= import('feature-event'));
    this._panelInjector?.destroy();
    this._panelInjector = this._host.createPanelInjector(portal, [EventPanelService, EventAccessService]);
    this._panelInjector.get(EventPanelService).openPreviewById(action.targetId, action.apartmentId, {
      apartmentName: action.apartmentName ?? undefined
    });
  }
}
